/**
 * ANALYTICS (Intelligence). Website traffic, acquisition and conversions from
 * the one PostHog project, as aggregates. Product adoption and onboarding are
 * NOT read from PostHog (no healthcare data goes there): they are the counts on
 * Customers & Onboarding.
 */
import { useEffect, useState } from 'react';
import type { ConsoleClient, ConsoleOutcome } from '../platform/client.js';
import type { PlatformActor } from '../platform/credentials.js';
import {
  ANALYTICS_WINDOWS,
  FUNNEL,
  OTHER_CONVERSIONS,
  analyticsRequest,
  bounceRatePct,
  formatDuration,
  pctChange,
  type AnalyticsRead,
} from '../platform/analytics.js';

const STATE_TEXT: Record<string, string> = {
  not_configured:
    'PostHog is not connected in this deployment yet: it needs a read-only PostHog key limited to project 653536.',
  permission_denied:
    'PostHog refused the configured key. It must be limited to project 653536 with query:read.',
  unavailable: 'PostHog did not answer. Try again shortly.',
};

export function Analytics({ actor, client }: { actor: PlatformActor; client: ConsoleClient }) {
  const [windowDays, setWindowDays] = useState(30);
  const [read, setRead] = useState<ConsoleOutcome<AnalyticsRead> | null>(null);
  useEffect(() => {
    setRead(null);
    void client<AnalyticsRead>(analyticsRequest(windowDays), actor).then(setRead);
  }, [actor, client, windowDays]);

  const count = (event: string) =>
    read?.kind === 'ok' ? (read.value.conversions?.find((c) => c.event === event)?.count ?? 0) : 0;

  // Period-over-period label for a headline metric. "—" when there is no prior
  // baseline, so a zero prior never renders a misleading change.
  const changeNote = (current?: number, previous?: number) => {
    const p = pctChange(current ?? 0, previous ?? 0);
    if (p === null) return `no prior ${windowDays}d baseline`;
    return `${p >= 0 ? '+' : ''}${p}% vs previous ${windowDays}d`;
  };

  return (
    <section>
      <form className="filters" onSubmit={(e) => e.preventDefault()}>
        <div className="field">
          <label htmlFor="anWindow">Period</label>
          <select
            id="anWindow"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
          >
            {ANALYTICS_WINDOWS.map((d) => (
              <option key={d} value={d}>
                last {d} days
              </option>
            ))}
          </select>
        </div>
      </form>
      <p className="quiet">
        Source: PostHog project 653536, production website events only, test traffic excluded.
        Product adoption and onboarding are on Customers &amp; Onboarding: no product or healthcare
        data is sent to PostHog.
      </p>
      {read?.kind === 'ok' && read.value.measuredAt ? (
        <p className="quiet" data-testid="analytics-asof">
          As of {new Date(read.value.measuredAt).toLocaleString()} (cached up to 5 minutes).
        </p>
      ) : null}
      {read === null ? (
        <p className="quiet">Reading PostHog…</p>
      ) : read.kind !== 'ok' ? (
        <div className="banner" role="alert">
          <strong>Analytics did not answer.</strong> {read.message}
        </div>
      ) : read.value.state !== 'connected' ? (
        <div className="banner" role="status">
          {STATE_TEXT[read.value.state] ?? read.value.state}
        </div>
      ) : !read.value.collecting ? (
        <div className="banner" role="status">
          Connected, and no production website events have been received yet. Public tracking is
          switched off until the privacy text is approved, so this is expected; nothing here is
          being shown as zero traffic.
        </div>
      ) : (
        <>
          <div className="metrics">
            <div className="metric">
              <span className="metric-label">Visitors</span>
              <span className="metric-value">{read.value.totals?.visitors ?? 0}</span>
              <span className="metric-note">
                distinct people, last {windowDays} days ·{' '}
                {changeNote(read.value.totals?.visitors, read.value.previousTotals?.visitors)}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Page views</span>
              <span className="metric-value">{read.value.totals?.pageviews ?? 0}</span>
              <span className="metric-note">
                {changeNote(read.value.totals?.pageviews, read.value.previousTotals?.pageviews)} ·
                last event{' '}
                {read.value.lastEventAt ? new Date(read.value.lastEventAt).toLocaleString() : '—'}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Sessions</span>
              <span className="metric-value">{read.value.sessions?.count ?? 0}</span>
              <span className="metric-note">
                bounce{' '}
                {(() => {
                  const b = bounceRatePct(
                    read.value.sessions?.bounced ?? 0,
                    read.value.sessions?.count ?? 0,
                  );
                  return b === null ? '—' : `${b}%`;
                })()}{' '}
                · avg {formatDuration(read.value.sessions?.avgDurationSec ?? 0)}
              </span>
            </div>
            {OTHER_CONVERSIONS.slice(0, 2).map(([event, label]) => (
              <div className="metric" key={event}>
                <span className="metric-label">{label}</span>
                <span className="metric-value">{count(event)}</span>
              </div>
            ))}
          </div>
          {read.value.trend && read.value.trend.length > 0 ? (
            <figure className="trend-figure">
              <div className="trend visitors" aria-hidden="true">
                {read.value.trend.map((t) => (
                  <div key={t.day} className="trend-bar" title={`${t.day}: ${t.visitors} visitors`}>
                    <span
                      style={{
                        height: `${(t.visitors / Math.max(1, ...read.value.trend!.map((x) => x.visitors))) * 100}%`,
                      }}
                    />
                  </div>
                ))}
              </div>
              <figcaption className="quiet">Visitors per day</figcaption>
            </figure>
          ) : null}
          <h3>Founding application funnel</h3>
          <ol className="milestones">
            {FUNNEL.map(([event, label], i) => {
              const n = count(event);
              const first = count(FUNNEL[0][0]);
              return (
                <li key={event}>
                  <strong>{label}</strong> — {n}
                  {i > 0 && first > 0 ? (
                    <span className="quiet"> ({Math.round((n / first) * 100)}% of viewers)</span>
                  ) : null}
                </li>
              );
            })}
          </ol>
          <div className="two-col">
            <div>
              <h3>Acquisition sources</h3>
              <table className="stack">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Visitors</th>
                  </tr>
                </thead>
                <tbody>
                  {(read.value.sources ?? []).map((s) => (
                    <tr key={s.source}>
                      <td data-label="Source">{s.source === '$direct' ? 'direct' : s.source}</td>
                      <td data-label="Visitors">{s.visitors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <h3>Top pages</h3>
              <table className="stack">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>Views</th>
                  </tr>
                </thead>
                <tbody>
                  {(read.value.pages ?? []).map((p) => (
                    <tr key={p.path}>
                      <td data-label="Page" className="mono">
                        {p.path}
                      </td>
                      <td data-label="Views">{p.views}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
